/**
 * Service de vérification des photos anti-fraude
 * Analyse les métadonnées EXIF pour détecter les photos suspectes (IA, montages, screenshots)
 */

import EXIF from 'exif-js';

// Dimensions typiques des images générées par IA
const AI_IMAGE_DIMENSIONS = [
  { w: 1024, h: 1024 },  // DALL-E, Midjourney square
  { w: 1792, h: 1024 },  // DALL-E landscape
  { w: 1024, h: 1792 },  // DALL-E portrait
  { w: 512, h: 512 },    // Stable Diffusion
  { w: 768, h: 768 },    // Stable Diffusion
  { w: 1280, h: 720 },   // HD screenshot
  { w: 1920, h: 1080 },  // Full HD screenshot
  { w: 2048, h: 2048 },  // High-res AI
  { w: 1536, h: 1024 },  // GPT-4 image
  { w: 1024, h: 1536 },  // GPT-4 image portrait
];

/**
 * Extrait les métadonnées EXIF d'une image
 * IMPORTANT: Chaque appel utilise un élément img unique et nettoie le cache EXIF
 * pour éviter les incohérences entre photos successives.
 * @param {File} file - Le fichier image
 * @returns {Promise<Object>} Les métadonnées extraites
 */
export const extractExifData = (file) => {
  return new Promise((resolve) => {
    // Timeout de sécurité (5 secondes max)
    const timeout = setTimeout(() => {
      console.log('[EXIF] Timeout pour:', file.name);
      resolve({ error: 'timeout', hasExif: false, filename: file.name });
    }, 5000);

    // ID unique pour tracer cette analyse
    const analysisId = `${file.name}_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;

    try {
      const reader = new FileReader();
      reader.onload = function(e) {
        // Créer un élément img UNIQUE pour chaque fichier
        const img = document.createElement('img');
        // Ajouter un attribut unique pour éviter tout cache navigateur
        img.setAttribute('data-analysis-id', analysisId);
        
        img.onload = function() {
          // CRUCIAL: Nettoyer tout cache EXIF précédent sur cet élément
          // La bibliothèque exif-js stocke les données dans img.exifdata
          if (img.exifdata) {
            delete img.exifdata;
          }
          if (img.iptcdata) {
            delete img.iptcdata;
          }
          
          EXIF.getData(img, function() {
            clearTimeout(timeout);
            
            // Extraire les métadonnées DEPUIS CET ÉLÉMENT SPÉCIFIQUE
            const allMetaData = EXIF.getAllTags(this);
            const tagCount = Object.keys(allMetaData).length;
            
            const dateOriginal = EXIF.getTag(this, 'DateTimeOriginal');
            const make = EXIF.getTag(this, 'Make'); // Fabricant (Apple, Samsung, etc.)
            const model = EXIF.getTag(this, 'Model'); // Modèle (iPhone 14, Galaxy S23, etc.)
            const software = EXIF.getTag(this, 'Software');
            const gpsLat = EXIF.getTag(this, 'GPSLatitude');
            const gpsLon = EXIF.getTag(this, 'GPSLongitude');
            const imageWidth = EXIF.getTag(this, 'PixelXDimension') || EXIF.getTag(this, 'ImageWidth') || img.naturalWidth;
            const imageHeight = EXIF.getTag(this, 'PixelYDimension') || EXIF.getTag(this, 'ImageHeight') || img.naturalHeight;
            const orientation = EXIF.getTag(this, 'Orientation');
            const exposureTime = EXIF.getTag(this, 'ExposureTime');
            const fNumber = EXIF.getTag(this, 'FNumber');
            const iso = EXIF.getTag(this, 'ISOSpeedRatings');
            const focalLength = EXIF.getTag(this, 'FocalLength');
            
            // Log de debug pour tracer l'analyse
            console.log(`[EXIF] Analyse ${analysisId}:`, {
              filename: file.name,
              tagCount,
              hasExif: tagCount > 0,
              make,
              model,
              hasCameraData: !!(exposureTime || fNumber || iso || focalLength),
              dimensions: `${imageWidth}x${imageHeight}`
            });
            
            const result = {
              hasExif: tagCount > 0,
              dateOriginal: dateOriginal || null,
              make: make || null,
              model: model || null,
              software: software || null,
              hasGPS: !!(gpsLat && gpsLon),
              gpsLat: gpsLat ? convertDMSToDD(gpsLat, EXIF.getTag(this, 'GPSLatitudeRef')) : null,
              gpsLon: gpsLon ? convertDMSToDD(gpsLon, EXIF.getTag(this, 'GPSLongitudeRef')) : null,
              allTags: tagCount,
              imageWidth: imageWidth,
              imageHeight: imageHeight,
              orientation: orientation,
              // Données de caméra (absentes sur photos IA)
              hasCameraData: !!(exposureTime || fNumber || iso || focalLength),
              exposureTime: exposureTime,
              fNumber: fNumber,
              iso: iso,
              focalLength: focalLength,
              // Métadonnée de traçabilité
              _analysisId: analysisId
            };
            
            // Nettoyer l'élément après extraction
            if (this.exifdata) delete this.exifdata;
            if (this.iptcdata) delete this.iptcdata;
            
            resolve(result);
          });
        };
        img.onerror = () => {
          clearTimeout(timeout);
          console.log('[EXIF] Erreur chargement image:', file.name);
          resolve({ error: 'image_load_error', hasExif: false, filename: file.name });
        };
        img.src = e.target.result;
      };
      reader.onerror = () => {
        clearTimeout(timeout);
        console.log('[EXIF] Erreur lecture fichier:', file.name);
        resolve({ error: 'file_read_error', hasExif: false, filename: file.name });
      };
      reader.readAsDataURL(file);
    } catch (error) {
      clearTimeout(timeout);
      console.log('[EXIF] Exception:', file.name, error.message);
      resolve({ error: error.message, hasExif: false, filename: file.name });
    }
  });
};

/**
 * Convertit les coordonnées GPS DMS en degrés décimaux
 */
const convertDMSToDD = (dms, ref) => {
  if (!dms || dms.length < 3) return null;
  const degrees = dms[0] + dms[1] / 60 + dms[2] / 3600;
  return (ref === 'S' || ref === 'W') ? -degrees : degrees;
};

/**
 * Vérifie si les dimensions correspondent à une image IA typique
 */
const isAIDimensions = (width, height) => {
  if (!width || !height) return false;
  
  // Vérifier les dimensions exactes connues des IA
  for (const dim of AI_IMAGE_DIMENSIONS) {
    if ((width === dim.w && height === dim.h) || (width === dim.h && height === dim.w)) {
      return true;
    }
  }
  
  // Vérifier si c'est un carré parfait (très courant pour les IA)
  if (width === height && width >= 512 && width <= 2048) {
    return true;
  }
  
  // Ratios suspects (exactement 16:9, 4:3, 1:1)
  const ratio = width / height;
  const suspectRatios = [1, 16/9, 9/16, 4/3, 3/4, 1.75, 0.571]; // 1792/1024 = 1.75
  for (const r of suspectRatios) {
    if (Math.abs(ratio - r) < 0.001) {
      // Ratio exact = suspect
      return true;
    }
  }
  
  return false;
};

/**
 * Analyse une photo et retourne un score de confiance
 * ATTENTION: Cette fonction doit être appelée SÉQUENTIELLEMENT pour chaque fichier
 * afin d'éviter les race conditions sur l'extraction EXIF.
 * @param {File} file - Le fichier image
 * @param {Date} taskDate - La date de la mission
 * @returns {Promise<Object>} Résultat de l'analyse
 */
export const analyzePhoto = async (file, taskDate) => {
  // ID unique pour cette analyse
  const analysisId = `analyze_${file.name}_${Date.now()}`;
  console.log(`[ANTI-FRAUDE] Début analyse: ${analysisId}`);
  
  const exif = await extractExifData(file);
  
  console.log(`[ANTI-FRAUDE] EXIF extrait pour ${file.name}:`, {
    hasExif: exif.hasExif,
    allTags: exif.allTags,
    make: exif.make,
    model: exif.model,
    hasCameraData: exif.hasCameraData
  });
  
  const result = {
    trustScore: 100, // Score de confiance (0-100)
    warnings: [],
    alerts: [],
    exifData: exif,
    verdict: 'ok', // ok, warning, suspect
    _analysisId: analysisId
  };

  let aiIndicators = 0; // Compteur d'indicateurs IA

  // 1. Vérifier si EXIF existe - CRITIQUE pour détection IA
  if (!exif.hasExif || exif.allTags < 3) {
    result.trustScore -= 50;
    aiIndicators += 2;
    result.alerts.push({
      code: 'NO_EXIF',
      message: 'Aucune métadonnée EXIF - Forte probabilité de photo IA ou modifiée',
      severity: 'critical'
    });
  }

  // 2. Vérifier le fabricant/modèle de l'appareil - CRITIQUE
  if (!exif.make && !exif.model) {
    result.trustScore -= 30;
    aiIndicators += 1;
    result.alerts.push({
      code: 'NO_DEVICE',
      message: 'Aucun appareil photo identifié - Photo probablement non prise par téléphone',
      severity: 'high'
    });
  }

  // 3. Vérifier les données de caméra (exposition, ISO, etc.) - Les IA n'ont JAMAIS ça
  if (!exif.hasCameraData) {
    result.trustScore -= 25;
    aiIndicators += 1;
    result.warnings.push({
      code: 'NO_CAMERA_DATA',
      message: 'Aucune donnée de prise de vue (ISO, exposition) - Suspect',
      severity: 'high'
    });
  }

  // 4. Vérifier les dimensions typiques des images IA
  if (isAIDimensions(exif.imageWidth, exif.imageHeight)) {
    result.trustScore -= 30;
    aiIndicators += 2;
    result.alerts.push({
      code: 'AI_DIMENSIONS',
      message: `Dimensions suspectes (${exif.imageWidth}x${exif.imageHeight}) - Typique des images IA`,
      severity: 'critical'
    });
  }

  // 5. Vérifier la date de prise de vue
  if (exif.dateOriginal && taskDate) {
    const photoDate = parseExifDate(exif.dateOriginal);
    if (photoDate) {
      const taskDateObj = new Date(taskDate);
      const diffHours = Math.abs(photoDate - taskDateObj) / (1000 * 60 * 60);
      
      if (diffHours > 48) {
        result.trustScore -= 30;
        result.alerts.push({
          code: 'DATE_MISMATCH',
          message: `Photo prise le ${photoDate.toLocaleDateString('fr-FR')} - différente de la mission`,
          severity: 'high'
        });
      } else if (diffHours > 24) {
        result.trustScore -= 15;
        result.warnings.push({
          code: 'DATE_WARNING',
          message: 'Date de la photo légèrement décalée',
          severity: 'medium'
        });
      }
    }
  } else if (!exif.dateOriginal) {
    result.trustScore -= 20;
    aiIndicators += 1;
    result.warnings.push({
      code: 'NO_DATE',
      message: 'Date de prise de vue absente',
      severity: 'medium'
    });
  }

  // 6. Vérifier le logiciel (souvent présent sur les images modifiées ou IA)
  if (exif.software) {
    const suspiciousSoftware = [
      'photoshop', 'gimp', 'canva', 'pixlr', 'snapseed', 'lightroom',
      'dall-e', 'midjourney', 'stable diffusion', 'ai', 'generated',
      'openai', 'chatgpt', 'bing', 'copilot'
    ];
    const softwareLower = exif.software.toLowerCase();
    if (suspiciousSoftware.some(s => softwareLower.includes(s))) {
      result.trustScore -= 40;
      aiIndicators += 2;
      result.alerts.push({
        code: 'AI_SOFTWARE',
        message: `Photo générée ou modifiée avec ${exif.software}`,
        severity: 'critical'
      });
    }
  }

  // 7. Vérifier la taille du fichier
  const fileSizeKB = file.size / 1024;
  if (fileSizeKB < 100) {
    result.trustScore -= 20;
    result.warnings.push({
      code: 'TINY_FILE',
      message: 'Fichier très petit - possible screenshot ou image compressée',
      severity: 'medium'
    });
  } else if (fileSizeKB > 5000 && !exif.hasExif) {
    // Gros fichier sans EXIF = très suspect
    result.trustScore -= 15;
    aiIndicators += 1;
    result.warnings.push({
      code: 'LARGE_NO_EXIF',
      message: 'Gros fichier sans métadonnées - Suspect',
      severity: 'medium'
    });
  }

  // 8. Vérification combinée - Si plusieurs indicateurs IA, c'est très probablement une IA
  if (aiIndicators >= 3) {
    result.trustScore = Math.min(result.trustScore, 20);
    result.alerts.unshift({
      code: 'AI_DETECTED',
      message: '🤖 PHOTO PROBABLEMENT GÉNÉRÉE PAR IA - Plusieurs indicateurs détectés',
      severity: 'critical'
    });
  }

  // Calculer le verdict final
  if (result.trustScore < 40 || aiIndicators >= 3) {
    result.verdict = 'suspect';
  } else if (result.trustScore < 70) {
    result.verdict = 'warning';
  } else {
    result.verdict = 'ok';
  }

  // Ajouter le compteur d'indicateurs IA
  result.aiIndicators = aiIndicators;

  // Log final du résultat
  console.log(`[ANTI-FRAUDE] Résultat final pour ${file.name}:`, {
    trustScore: result.trustScore,
    aiIndicators: aiIndicators,
    verdict: result.verdict,
    alertsCount: result.alerts.length,
    warningsCount: result.warnings.length
  });

  return result;
};

/**
 * Parse une date EXIF au format "YYYY:MM:DD HH:MM:SS"
 */
const parseExifDate = (dateStr) => {
  if (!dateStr) return null;
  try {
    // Format EXIF: "2024:06:01 14:30:00"
    const [datePart, timePart] = dateStr.split(' ');
    const [year, month, day] = datePart.split(':');
    const [hour, min, sec] = (timePart || '00:00:00').split(':');
    return new Date(year, month - 1, day, hour, min, sec);
  } catch {
    return null;
  }
};

/**
 * Retourne une couleur et icône selon le verdict
 */
export const getVerdictDisplay = (verdict) => {
  switch (verdict) {
    case 'suspect':
      return {
        color: 'red',
        bgColor: 'bg-red-100',
        textColor: 'text-red-700',
        borderColor: 'border-red-300',
        icon: '⚠️',
        label: 'Photo suspecte'
      };
    case 'warning':
      return {
        color: 'orange',
        bgColor: 'bg-orange-100',
        textColor: 'text-orange-700',
        borderColor: 'border-orange-300',
        icon: '⚡',
        label: 'À vérifier'
      };
    default:
      return {
        color: 'green',
        bgColor: 'bg-emerald-100',
        textColor: 'text-emerald-700',
        borderColor: 'border-emerald-300',
        icon: '✓',
        label: 'Photo authentique'
      };
  }
};

export default {
  extractExifData,
  analyzePhoto,
  getVerdictDisplay
};
